import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatNumber } from "@/lib/format";

type Stats = components["schemas"]["AdminAgentStatsResponse"];

interface AgentConversationStatsProps {
  organisationId: string;
}

export function AgentConversationStats({ organisationId }: AgentConversationStatsProps) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .GET("/admin/organisations/{organisationId}/agent/stats", {
        params: { path: { organisationId } },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setStats(data ?? null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }

  if (!stats) return null;

  const byAgentType = Object.entries(stats.conversationsByAgentType ?? {});
  const byRole = Object.entries(stats.messagesByRole ?? {});

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent Usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Conversations" value={formatNumber(stats.totalConversations)} />
          <Stat label="Messages" value={formatNumber(stats.totalMessages)} />
          <Stat label="Active Users" value={formatNumber(stats.activeUserCount)} />
          <Stat label="Truncated Msgs" value={formatNumber(stats.truncatedMessageCount)} />
          <Stat label="Input Tokens" value={formatNumber(stats.totalInputTokens)} />
          <Stat label="Output Tokens" value={formatNumber(stats.totalOutputTokens)} />
        </div>

        {byAgentType.length > 0 && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              By Agent Type
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 text-sm">
              {byAgentType.map(([key, value]) => (
                <div key={key}>
                  <dt className="text-muted-foreground">{key}</dt>
                  <dd className="tabular-nums">{formatNumber(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {byRole.length > 0 && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              By Role
            </h3>
            <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 text-sm">
              {byRole.map(([key, value]) => (
                <div key={key}>
                  <dt className="text-muted-foreground">{key}</dt>
                  <dd className="tabular-nums">{formatNumber(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs uppercase tracking-wider">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
